<script setup>
// The test conversation with the assistant (phase 4.6), on the plant of the kit's Preview (agent/CompanionPanel.vue):
// the kit's Empty until the first question, the turns as the kit's MessageBubble (the person on grey, the assistant on
// the Polaris gradient with its mark and name), and the kit's compact Composer (Enter sends) with the note that these
// messages count toward the credits. Same call (PolarisAssistant.playground), same history, same reset when the
// assistant changes; `reset` is exposed for the page header's button.
import { nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { View16 } from '@carbon/icons-vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import PolarisAssistant from 'dashboard/api/polaris/assistant';
import { prettyToolName } from './runDetailsFormat';
import MessageBubble from 'dashboard/components-next/polaris/cards/MessageBubble.vue';
import Composer from 'dashboard/components-next/polaris/cards/Composer.vue';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';

const { assistantId, assistantName, contactId } = defineProps({
  assistantId: {
    type: Number,
    required: true,
  },
  assistantName: {
    type: String,
    default: '',
  },
  // "Testando como contato existente" (Fase 7): vai como contact_id no run; null = lead novo.
  contactId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['runDetails']);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();
const messages = ref([]);
const newMessage = ref('');
const isLoading = ref(false);
const thread = ref(null);

const formatMessagesForApi = () => {
  return messages.value.map(message => {
    const payload = {
      role: message.sender,
      content: message.content,
    };

    if (message.sender === 'assistant' && message.agentName) {
      payload.agent_name = message.agentName;
    }

    return payload;
  });
};

const resetConversation = () => {
  messages.value = [];
  newMessage.value = '';
};

// Watch for assistant ID changes and reset conversation
watch(
  () => assistantId,
  (newId, oldId) => {
    if (oldId && newId !== oldId) {
      resetConversation();
    }
  }
);

// The newest turn stays in sight, as the woot's MessageList did.
const scrollToBottom = async () => {
  await nextTick();
  if (thread.value) thread.value.scrollTop = thread.value.scrollHeight;
};
watch(() => [messages.value.length, isLoading.value], scrollToBottom);

const sendMessage = async () => {
  if (!newMessage.value.trim() || isLoading.value) return;

  const userMessage = {
    content: newMessage.value,
    sender: 'user',
    timestamp: new Date().toISOString(),
  };
  messages.value.push(userMessage);
  const currentMessage = newMessage.value;
  newMessage.value = '';

  try {
    isLoading.value = true;
    const { data } = await PolarisAssistant.playground({
      assistantId,
      messageContent: currentMessage,
      messageHistory: formatMessagesForApi(),
      contactId,
    });

    messages.value.push({
      content: data.response,
      sender: 'assistant',
      agentName: data.agent_name,
      toolsCalled: data.run_details?.tools_called || [],
      timestamp: new Date().toISOString(),
    });
    if (data.run_details) emit('runDetails', data.run_details);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting assistant response:', error);
  } finally {
    isLoading.value = false;
  }
};

const authorOf = message =>
  message.sender === 'user'
    ? t('POLARIS.PLAYGROUND.USER')
    : message.agentName || assistantName || t('POLARIS.PLAYGROUND.ASSISTANT');

defineExpose({ reset: resetConversation });
</script>

<template>
  <div
    class="flex h-full min-h-0 flex-col overflow-hidden rounded-polaris-xl border border-solid border-polaris-border bg-polaris-card"
    data-playground
  >
    <div
      ref="thread"
      class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
      aria-live="polite"
    >
      <Empty v-if="!messages.length" class="flex-1 border-0">
        <EmptyHeader>
          <EmptyMedia variant="icon"><View16 aria-hidden="true" /></EmptyMedia>
          <EmptyTitle>
            {{ t('POLARIS.PAGES.PLAYGROUND.EMPTY_TITLE') }}
          </EmptyTitle>
          <EmptyDescription>
            {{ t('POLARIS.PAGES.PLAYGROUND.EMPTY_DESCRIPTION') }}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
      <div v-for="(message, index) in messages" :key="index" class="contents">
        <div v-if="message.toolsCalled?.length" class="flex flex-wrap gap-1.5">
          <span
            v-for="tool in message.toolsCalled"
            :key="tool"
            class="rounded-full border border-polaris-border px-2 py-0.5 text-xs text-polaris-muted-foreground"
          >
            {{ prettyToolName(tool) }}
          </span>
        </div>
        <MessageBubble
          :from="message.sender === 'user' ? 'customer' : 'agent'"
          :author="authorOf(message)"
        >
          <div
            class="break-words [&_p]:m-0"
            v-html="formatMessage(message.content)"
          />
        </MessageBubble>
      </div>
      <MessageBubble
        v-if="isLoading"
        from="agent"
        :author="assistantName || t('POLARIS.PLAYGROUND.ASSISTANT')"
      >
        <span
          class="flex gap-1 py-1"
          :aria-label="t('POLARIS.PAGES.PLAYGROUND.TYPING')"
          role="status"
        >
          <span
            class="size-2 animate-bounce rounded-full bg-current motion-reduce:animate-none"
          />
          <span
            class="size-2 animate-bounce rounded-full bg-current motion-reduce:animate-none [animation-delay:0.2s]"
          />
          <span
            class="size-2 animate-bounce rounded-full bg-current motion-reduce:animate-none [animation-delay:0.4s]"
          />
        </span>
      </MessageBubble>
    </div>
    <div class="grid shrink-0 gap-2 p-4 pt-0">
      <Composer
        v-model="newMessage"
        compact
        :rows="2"
        :label="t('POLARIS.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        :submit-label="t('POLARIS.PAGES.PLAYGROUND.SEND')"
        :placeholder="t('POLARIS.PLAYGROUND.MESSAGE_PLACEHOLDER')"
        @submit="sendMessage"
      />
      <p class="text-center type-polaris-caption text-polaris-muted-foreground">
        {{ t('POLARIS.PLAYGROUND.CREDIT_NOTE') }}
      </p>
    </div>
  </div>
</template>
